import { queryOptions, type UndefinedInitialQueryOptions } from '@tanstack/vue-query'
import { api } from '@/services'
import { unwrapApiResponse } from '@queries/shared/unwrap-api-response'
import type {
  LedgerAdjustmentQueryReqType,
  LedgerAbnormalResType,
  LedgerAdjustmentRecordResType,
  LedgerCorrectionResType
} from '@/models/types/ledgerAnomaly.types'
import { ledgerAnomalyKeys } from './keys'
// 後端假資料開關定義在測試用的 mock-egm-mapping.ts，接上真資料時兩者會一起消失
import { USE_MOCK_LEDGER_ANOMALY, thinMockAbnormals, thinMockCorrections } from './mock-egm-mapping'

/**
 * 三支 GET 共用：補上後端假資料開關，並在查全店時剔除 egmId。
 *
 * 查全店要完全不帶 egmId 這個欄位，送 `egmId=` 會多一個無意義的查詢參數。
 */
const toRequestParams = (params: LedgerAdjustmentQueryReqType): LedgerAdjustmentQueryReqType => {
  const { egmId, ...rest } = params

  return {
    ...rest,
    ...(egmId ? { egmId } : {}),
    useMockData: USE_MOCK_LEDGER_ANOMALY
  }
}

type AbnormalQueryOptions = UndefinedInitialQueryOptions<
  LedgerAbnormalResType[],
  Error,
  LedgerAbnormalResType[],
  ReturnType<typeof ledgerAnomalyKeys.abnormal>
>

/**
 * 系統偵測出的異常事件。畫面的待確認筆數與排除標示都以這支為準。
 *
 * 一筆事件會依指標回多列，不做併列——判定本來就是針對單一指標。
 */
export const getLedgerAbnormalQueryOptions = (
  params: LedgerAdjustmentQueryReqType
): AbnormalQueryOptions =>
  queryOptions({
    queryKey: ledgerAnomalyKeys.abnormal(params),
    queryFn: async ({ signal }): Promise<LedgerAbnormalResType[]> => {
      const response = await api.store.getAccountingAdjustmentAbnormal(
        toRequestParams(params),
        signal
      )
      // MOCK_EGM_MAPPING：測試用，接上真資料後這層直接拿掉
      return thinMockAbnormals(unwrapApiResponse<LedgerAbnormalResType[]>(response) ?? [])
    }
  })

type RecordQueryOptions = UndefinedInitialQueryOptions<
  LedgerAdjustmentRecordResType[],
  Error,
  LedgerAdjustmentRecordResType[],
  ReturnType<typeof ledgerAnomalyKeys.record>
>

/** MI 計算過的每事件 delta，逐筆事件明細的資料來源 */
export const getLedgerRecordQueryOptions = (
  params: LedgerAdjustmentQueryReqType
): RecordQueryOptions =>
  queryOptions({
    queryKey: ledgerAnomalyKeys.record(params),
    queryFn: async ({ signal }): Promise<LedgerAdjustmentRecordResType[]> => {
      const response = await api.store.getAccountingAdjustmentRecord(
        toRequestParams(params),
        signal
      )
      return unwrapApiResponse<LedgerAdjustmentRecordResType[]>(response) ?? []
    }
  })

type CorrectionQueryOptions = UndefinedInitialQueryOptions<
  LedgerCorrectionResType[],
  Error,
  LedgerCorrectionResType[],
  ReturnType<typeof ledgerAnomalyKeys.correction>
>

/** 修正紀錄。日列的「曾修正」記號以這支是否有該日資料為準 */
export const getLedgerCorrectionQueryOptions = (
  params: LedgerAdjustmentQueryReqType
): CorrectionQueryOptions =>
  queryOptions({
    queryKey: ledgerAnomalyKeys.correction(params),
    queryFn: async ({ signal }): Promise<LedgerCorrectionResType[]> => {
      const response = await api.store.getAccountingAdjustmentCorrection(
        toRequestParams(params),
        signal
      )
      // MOCK_EGM_MAPPING：測試用，接上真資料後這層直接拿掉
      return thinMockCorrections(unwrapApiResponse<LedgerCorrectionResType[]>(response) ?? [])
    }
  })
